// A ZIP reader, because an .xlsx IS a zip of XML — nothing more exotic than
// that. No dependency: the browser already ships the only hard part.
//
// `DecompressionStream('deflate-raw')` is the inflater, which is what makes a
// hand-rolled reader ~150 lines instead of the 40 kB of a bundled one. It is in
// every browser this app runs in (Chrome 80, Safari 16.4, Firefox 113) and in
// Node 18, so the tests exercise the same path the app does.
//
// Entries are read LAZILY. A real workbook carries images, charts, printer
// settings and a theme; inflating all of them to read three XML parts is work
// nobody asked for, and on a 30 MB file it is the difference between a pause
// and a hang.

/**
 * Bytes backed by a plain ArrayBuffer, which is what a File read gives us.
 * Spelled out because `DecompressionStream` will not take the SharedArrayBuffer
 * case that a bare `Uint8Array` also covers.
 */
export type Bytes = Uint8Array<ArrayBuffer>;

/** One file in the archive, located but not yet inflated. */
interface Entry {
  name: string;
  /** 0 = stored, 8 = deflate. Anything else we cannot read. */
  method: number;
  /** Offset of the LOCAL header, which is where the data actually lives. */
  headerOffset: number;
  compressedSize: number;
  size: number;
}

export interface ZipArchive {
  names(): string[];
  has(name: string): boolean;
  /** Null when there is no such entry. Throws when the entry cannot be inflated. */
  bytes(name: string): Promise<Bytes | null>;
  text(name: string): Promise<string | null>;
}

const EOCD_SIG = 0x06054b50;
const EOCD64_LOCATOR_SIG = 0x07064b50;
const EOCD64_SIG = 0x06064b50;
const CENTRAL_SIG = 0x02014b50;
const LOCAL_SIG = 0x04034b50;

/** The comment at the end of a zip can be 64 kB, and the EOCD sits before it. */
const MAX_TRAILER = 66_000;

export function openZip(data: Bytes): ZipArchive {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const entries = readCentralDirectory(data, view);
  const byName = new Map(entries.map((e) => [e.name, e]));

  const bytes = async (name: string): Promise<Bytes | null> => {
    const entry = byName.get(name);
    if (!entry) return null;
    return readEntry(data, view, entry);
  };

  return {
    names: () => entries.map((e) => e.name),
    has: (name) => byName.has(name),
    bytes,
    async text(name) {
      const raw = await bytes(name);
      return raw === null ? null : new TextDecoder().decode(raw);
    },
  };
}

/** True when the bytes start with a local file header — the .xlsx sniff test. */
export function looksLikeZip(data: Bytes): boolean {
  return data.length > 4 && data[0] === 0x50 && data[1] === 0x4b && data[2] === 0x03 && data[3] === 0x04;
}

// ---------------------------------------------------------------------------

function readCentralDirectory(data: Bytes, view: DataView): Entry[] {
  const eocd = findEocd(view);
  if (eocd < 0) throw new Error('That file is not a zip archive.');

  let count = view.getUint16(eocd + 10, true);
  let start = view.getUint32(eocd + 16, true);

  // Zip64. Excel emits it for a workbook with a lot of parts, and the giveaway
  // is a field pegged at its maximum — the real value is in the zip64 record.
  if (count === 0xffff || start === 0xffffffff) {
    const locator = eocd - 20;
    if (locator >= 0 && view.getUint32(locator, true) === EOCD64_LOCATOR_SIG) {
      const at = Number(view.getBigUint64(locator + 8, true));
      if (view.getUint32(at, true) === EOCD64_SIG) {
        count = Number(view.getBigUint64(at + 32, true));
        start = Number(view.getBigUint64(at + 48, true));
      }
    }
  }

  const entries: Entry[] = [];
  let at = start;
  for (let i = 0; i < count; i++) {
    if (at + 46 > data.length || view.getUint32(at, true) !== CENTRAL_SIG) break;
    const method = view.getUint16(at + 10, true);
    const nameLen = view.getUint16(at + 28, true);
    const extraLen = view.getUint16(at + 30, true);
    const commentLen = view.getUint16(at + 32, true);
    const name = new TextDecoder().decode(data.subarray(at + 46, at + 46 + nameLen));
    const entry: Entry = {
      name,
      method,
      compressedSize: view.getUint32(at + 20, true),
      size: view.getUint32(at + 24, true),
      headerOffset: view.getUint32(at + 42, true),
    };
    // Same trick as above, one entry down: a 0xFFFFFFFF is a promise that the
    // real number is in the zip64 extra field.
    if (entry.size === 0xffffffff || entry.compressedSize === 0xffffffff || entry.headerOffset === 0xffffffff) {
      readZip64Extra(view, at + 46 + nameLen, extraLen, entry);
    }
    entries.push(entry);
    at += 46 + nameLen + extraLen + commentLen;
  }
  return entries;
}

/** The zip64 extra field carries the fields that overflowed, in a fixed order. */
function readZip64Extra(view: DataView, at: number, length: number, entry: Entry): void {
  const end = at + length;
  while (at + 4 <= end) {
    const id = view.getUint16(at, true);
    const size = view.getUint16(at + 2, true);
    if (id === 0x0001) {
      let field = at + 4;
      if (entry.size === 0xffffffff && field + 8 <= end) {
        entry.size = Number(view.getBigUint64(field, true));
        field += 8;
      }
      if (entry.compressedSize === 0xffffffff && field + 8 <= end) {
        entry.compressedSize = Number(view.getBigUint64(field, true));
        field += 8;
      }
      if (entry.headerOffset === 0xffffffff && field + 8 <= end) {
        entry.headerOffset = Number(view.getBigUint64(field, true));
      }
      return;
    }
    at += 4 + size;
  }
}

function findEocd(view: DataView): number {
  const from = Math.max(0, view.byteLength - MAX_TRAILER);
  for (let at = view.byteLength - 22; at >= from; at--) {
    if (view.getUint32(at, true) === EOCD_SIG) return at;
  }
  return -1;
}

async function readEntry(data: Bytes, view: DataView, entry: Entry): Promise<Bytes> {
  const at = entry.headerOffset;
  if (view.getUint32(at, true) !== LOCAL_SIG) throw new Error(`Damaged zip entry: ${entry.name}`);
  // The local header repeats the sizes, but a streaming writer leaves them at
  // zero and puts the truth in a trailing descriptor — so the sizes come from
  // the central directory and only the two LENGTHS are read here.
  const nameLen = view.getUint16(at + 26, true);
  const extraLen = view.getUint16(at + 28, true);
  const from = at + 30 + nameLen + extraLen;
  const raw = data.subarray(from, from + entry.compressedSize);

  if (entry.method === 0) return raw;
  if (entry.method !== 8) throw new Error(`Unsupported compression in ${entry.name}.`);
  return inflateRaw(raw);
}

async function inflateRaw(input: Bytes): Promise<Bytes> {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('This browser cannot unzip files, so it cannot read .xlsx.');
  }
  const stream = new DecompressionStream('deflate-raw');
  const writer = stream.writable.getWriter();
  // Written concurrently with the read below — awaiting the write first would
  // deadlock on anything larger than the stream's internal buffer. The catch is
  // not optional: a corrupt entry rejects BOTH ends, and an unhandled rejection
  // takes the process down in Node.
  const pumped = (async () => {
    await writer.write(input);
    await writer.close();
  })().catch(() => undefined);

  const reader = stream.readable.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    total += value.length;
  }
  await pumped;

  const out = new Uint8Array(total);
  let at = 0;
  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.length;
  }
  return out;
}
